import type {BetterSystemStyleObject} from '@primer/styled-react'
import {Box} from '@primer/styled-react'
import {Tooltip} from '@primer/react/deprecated'

import styles from './AheadBehindCount.module.css'
import {clsx} from 'clsx'

interface AheadBehindCountProps {
  aheadCount: number
  behindCount: number
  className?: string
  maxDiverged?: number
  width?: number
  sx?: BetterSystemStyleObject
}

export default function AheadBehindCount({
  aheadCount,
  behindCount,
  className,
  maxDiverged,
  width = AheadBehindCountWidth,
  sx,
}: AheadBehindCountProps) {
  const aheadBehindCount = aheadCount + behindCount
  // if maxDiverged is undefined (0 is falsey),
  // then calculate the percent relative to the sum of the aheadBehindCount
  const aheadPercent =
    maxDiverged !== undefined
      ? scaleAheadBehind(aheadCount, maxDiverged)
      : Math.floor((aheadCount / aheadBehindCount) * 100)
  const behindPercent = maxDiverged !== undefined ? scaleAheadBehind(behindCount, maxDiverged) : 100 - aheadPercent

  return (
    <Box
      sx={sx}
      aria-label={`Ahead behind count: ${aheadBehindCount}`}
      className={clsx(className, 'branch-a-b-count', styles.Container)}
      role="img"
    >
      <Tooltip aria-label={`Ahead behind count: ${aheadBehindCount}`}>
        <Box
          sx={{
            width,
          }}
          className={styles.ChartWrapper}
        >
          <CountHalf aheadOrBehind="behind" count={behindCount} percentage={behindPercent} />
          <CountHalf aheadOrBehind="ahead" count={aheadCount} percentage={aheadPercent} />
        </Box>
      </Tooltip>
    </Box>
  )
}

const CountHalf = ({
  aheadOrBehind,
  count,
  percentage,
}: {
  aheadOrBehind: 'ahead' | 'behind'
  count: number
  percentage: number
}) => (
  <div className={clsx(styles.CountHalf, aheadOrBehind === 'ahead' ? styles.CountHalfAhead : styles.CountHalfBehind)}>
    <div className={styles.CountLabel}>{count}</div>
    <Box
      data-testid={`ahead-behind-${percentage}`}
      sx={{
        width: `${percentage}%`,
      }}
      className={clsx(
        styles.BarContainer,
        aheadOrBehind === 'ahead' ? styles.BarContainerAhead : styles.BarContainerBehind,
      )}
    >
      <Box
        sx={{
          backgroundColor: count > 0 ? 'neutral.muted' : 'transparent',
        }}
        className={clsx(
          'anim-grow-x',
          styles.ProgressBar,
          aheadOrBehind === 'ahead' ? styles.ProgressBarAhead : styles.ProgressBarBehind,
        )}
      />
    </Box>
  </div>
)

export const AheadBehindCountWidth = 150

// Borrowed from app/view_models/branches/ahead_behind_view.rb
export function scaleAheadBehind(aheadBehindCount: number, maxDiverged: number): number {
  aheadBehindCount = aheadBehindCount <= 0 ? 0 : Math.log10(aheadBehindCount)
  maxDiverged = maxDiverged <= 0 ? 0 : Math.log10(maxDiverged)
  // Calculate where the number falls between 0 and max diverged
  // with an output range of 0 and 1
  const uninterpolatedNumber = maxDiverged > 0 ? aheadBehindCount / maxDiverged : 0
  // Calculate where the number is between 0 and 100
  const interpolatedNumber = uninterpolatedNumber * 100
  return parseFloat(interpolatedNumber.toFixed(2))
}

try{ AheadBehindCount.displayName ||= 'AheadBehindCount' } catch {}
try{ CountHalf.displayName ||= 'CountHalf' } catch {}